import { FC } from 'react';
import { useMutation } from '@apollo/client';
import history from 'service/customHistory';
import * as yup from 'yup';
import { Formik } from 'formik';
import * as K from 'common/constants';
import { CHANGE_PASSWORD_MUTATION } from 'components/changePassword/queries';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Input from 'components/common/Input/Input';

interface FormValues {
  currentPassword: string;
  newPassword: string;
}

const initialValues: FormValues = {
  currentPassword: '',
  newPassword: '',
};

const ChangePasswordForm: FC = () => {
  const [changePassword, { error, data: changePasswordResponse, loading }] = useMutation<{ changePassword: boolean }>(
    CHANGE_PASSWORD_MUTATION,
    {
      onCompleted: (data: { changePassword: boolean }) => {
        if (data.changePassword) {
          history.push(K.ROUTE.ROOT);
        }
      },
    },
  );

  const validationSchema = yup.object({
    currentPassword: yup.string().required('Current password is a required field'),
    newPassword: yup
      .string()
      .required('New password is a required field')
      .min(6, 'New password must be at least 6 characters '),
  });

  function requestPasswordChange(values: FormValues): void {
    const { currentPassword, newPassword } = values;
    changePassword({ variables: { currentPassword, newPassword, timezone: new Date().getTimezoneOffset() } });
  }

  const isInvalidPasswordResponse = changePasswordResponse ? !changePasswordResponse.changePassword : false;

  return (
    <Formik validationSchema={validationSchema} onSubmit={requestPasswordChange} initialValues={initialValues}>
      {({ handleChange, values, touched, errors, submitForm }): JSX.Element => (
        <div className="ChangePassword__Form">
          <Input
            label="Current Password"
            type="password"
            name="currentPassword"
            value={values.currentPassword}
            onChange={handleChange}
            isInvalid={(touched.currentPassword && !!errors.currentPassword) || isInvalidPasswordResponse}
            errorMessage={errors.currentPassword || (isInvalidPasswordResponse ? 'Current password is invalid' : '')}
            inputClassName="ChangePassword__FormInput"
            labelClassName="ChangePassword__FormField"
            labelTextClassName="ChangePassword__FormLabel"
            errorTextClassName="ChangePassword__FormInvalidText"
          />
          <Input
            label="New Password"
            type="password"
            name="newPassword"
            onChange={handleChange}
            value={values.newPassword}
            isInvalid={touched.newPassword && !!errors.newPassword}
            errorMessage={errors.newPassword}
            inputClassName="ChangePassword__FormInput"
            labelClassName="ChangePassword__FormField"
            labelTextClassName="ChangePassword__FormLabel"
            errorTextClassName="ChangePassword__FormInvalidText"
          />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={submitForm}
            className="ChangePassword__Button"
            disabled={loading}
          >
            Change Password
          </Button>
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {error?.message}
          </div>
        </div>
      )}
    </Formik>
  );
};

export default ChangePasswordForm;
